import { Paper, SegmentedControl, Stack, Switch, Title } from '@mantine/core'
import { UseFormReturnType } from '@mantine/form'
import { ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { HorizontalField } from '@/components/ui-shared/HorizontalField/HorizontalField'
import { PipelineType } from '@/types/vlm'
import { OutputIntegrationsSelector } from './OutputIntegrationsSelector'
import { PipelineFormValues } from './VlmPipelineForm'
import { PatrolSummaryCard } from './patrol/PatrolSummaryCard'

const FPS_OPTIONS = [1, 2, 4]

type SettingsCardProps = {
  title: ReactNode
  children: ReactNode
}

const SettingsCard = ({ title, children }: SettingsCardProps) => (
  <Paper p="lg" bg="gray.1">
    <Stack gap="md">
      <Title order={5} fz="sm">
        {title}
      </Title>

      {children}
    </Stack>
  </Paper>
)

type PipelineSettingsStepProps = {
  form: UseFormReturnType<PipelineFormValues>
}

export const PipelineSettingsStep = ({ form }: PipelineSettingsStepProps) => {
  return (
    <Stack gap="xl">
      {form.values.pipelineType === 'virtual_patrol' && (
        <PatrolSummaryCard form={form} />
      )}

      <SettingsCard
        title={
          <FormattedMessage id="vlm.pipelines.settings.processing.title" />
        }
      >
        <Stack gap="xl">
          <HorizontalField
            size="sm"
            label={<FormattedMessage id="vlm.pipelines.settings.fps.label" />}
            description={
              <FormattedMessage id="vlm.pipelines.settings.fps.description" />
            }
          >
            <SegmentedControl
              size="sm"
              color="brand-primary"
              styles={{ root: { backgroundColor: 'white' } }}
              value={String(form.values.fps)}
              data={FPS_OPTIONS.map(String)}
              fullWidth
              onChange={(value) =>
                form.setFieldValue('fps', parseInt(value, 10))
              }
            />
          </HorizontalField>

          <HorizontalField
            size="sm"
            label={
              <FormattedMessage id="vlm.pipelines.settings.thinking.label" />
            }
            description={
              <FormattedMessage id="vlm.pipelines.settings.thinking.description" />
            }
          >
            <Paper py="xs" px="md">
              <Switch
                size="sm"
                {...form.getInputProps('enableThinking', { type: 'checkbox' })}
              />
            </Paper>
          </HorizontalField>
        </Stack>
      </SettingsCard>

      <SettingsCard
        title={
          <FormattedMessage id="vlm.pipelines.settings.outputIntegrations.title" />
        }
      >
        <OutputIntegrationsSelector
          pipelineType={form.values.pipelineType as PipelineType}
          uploadDescriptions={form.values.source.uploadDescriptions}
          enableCloudResults={form.values.enableCloudResults}
          onUploadDescriptionsChange={(value) =>
            form.setFieldValue('source.uploadDescriptions', value)
          }
          onEnableCloudResultsChange={(value) =>
            form.setFieldValue('enableCloudResults', value)
          }
        />
      </SettingsCard>
    </Stack>
  )
}
